import type { SignupFirstTouch, SiteVisitRequest } from "@scalephant/contract";

/**
 * Scalephant's own pageview count (SP-045, docs/oma-markkinointi/implementation_plan.md).
 *
 * No cookie and no browser storage, so no consent banner: the first page's referrer and
 * campaign tags live in this module's memory for as long as the tab does, which is long
 * enough to ride along with a sign-up made in the same visit. The server works out country
 * and a once-a-day visitor hash itself and keeps no address.
 */

const ENDPOINT = "/api/v1/site-visits";
const UTM_KEYS = [
  ["utm_source", "utmSource"],
  ["utm_medium", "utmMedium"],
  ["utm_campaign", "utmCampaign"],
  ["utm_content", "utmContent"],
  ["utm_term", "utmTerm"],
] as const;

let firstTouch: SignupFirstTouch | null = null;
let lastPath: string | null = null;

/** Visitors who asked not to be followed, and automated browsers, are not counted. */
function optedOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true || nav.doNotTrack === "1" || nav.webdriver;
}

function campaignTags(search: string): Partial<SignupFirstTouch> {
  const params = new URLSearchParams(search);
  const tags: Record<string, string> = {};
  for (const [param, key] of UTM_KEYS) {
    const value = params.get(param)?.trim().slice(0, 200);
    if (value) tags[key] = value;
  }
  return tags;
}

/** Counts one public page. Called on every route change; `/app` pages are never counted. */
export function trackPublicPage(path: string): void {
  if (path.startsWith("/app") || path === lastPath) return;
  lastPath = path;
  const entry = firstTouch === null;
  if (entry) {
    const referrer = document.referrer.slice(0, 2000);
    firstTouch = {
      landingPath: path.slice(0, 500),
      ...(referrer === "" ? {} : { referrer }),
      ...campaignTags(window.location.search),
    };
  }
  if (optedOut() || typeof navigator.sendBeacon !== "function") return;
  const body: SiteVisitRequest = entry
    ? {
        path: path.slice(0, 500),
        ...(firstTouch?.referrer === undefined ? {} : { referrer: firstTouch.referrer }),
        ...campaignTags(window.location.search),
        entry: true,
      }
    : { path: path.slice(0, 500) };
  try {
    navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(body)], { type: "application/json" }));
  } catch {
    // A count that does not arrive is not worth an error in somebody's console.
  }
}

/** Where this visit began, for the sign-up request; undefined when the tab began in the app. */
export function firstTouchForSignup(): SignupFirstTouch | undefined {
  if (firstTouch === null || optedOut()) return undefined;
  return firstTouch;
}

/** Tests only. */
export function resetVisitBeacon(): void {
  firstTouch = null;
  lastPath = null;
}
